import { hasWritePermission } from '../utils/biblioteca-actions'
import { SelectedItemBiblioteca } from '@/modules/biblioteca-virtual/types/types'
import { useLazyQuery } from '@apollo/client'
import {
  DownloadBatchQuery,
  DownloadBatchQueryVariables
} from '@/core/types/graphql'
import { DOWNLOAD_BATCH } from '@/modules/biblioteca-virtual/data/download-batch'
import { useEffect, useState } from 'react'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { t } from '@lingui/core/macro'
import { downloadFiles } from '@/modules/biblioteca-virtual/utils/functions'

export const useToolbarActions = (selectedRows: SelectedItemBiblioteca[]) => {
  const [downloadBatch] = useLazyQuery<
    DownloadBatchQuery,
    DownloadBatchQueryVariables
  >(DOWNLOAD_BATCH)
  const { snackbar } = useSnackbar()
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (loading) {
      snackbar.show('info', t`Aguarde enquanto geramos o seu download`)
    }
  }, [loading, snackbar])

  const handleDownload = async () => {
    setLoading(true)
    try {
      const { data } = await downloadBatch({
        variables: {
          ids: selectedRows.map(it => it.data.id)
        }
      })
      const singleFile =
        selectedRows.length === 1 && !selectedRows[0].data.isPasta
      await downloadFiles(data?.downloadBatch, singleFile)
    } catch (e) {
      snackbar.show(
        'error',
        t`Houve um erro enquanto tentamos processar o seu download`
      )
    } finally {
      setLoading(false)
    }
  }

  const arquivosSelecionados = selectedRows.filter(
    row => row.selected && !row.data.isPasta
  )
  const pastasSelecionadas = selectedRows.filter(
    row => row.selected && row.data.isPasta
  )

  const isSelected = selectedRows.filter(row => row.selected)
  const hasPastaPai = selectedRows.filter(
    row => row.selected && row.data.pastaPai !== null
  )
  const hasPermissionWrite = () => {
    const permissionWrite = selectedRows.filter(
      row => row.selected && hasWritePermission(row.data)
    )

    return isSelected.every(row =>
      permissionWrite.some(
        rowPermission => row.data.id === rowPermission.data.id
      )
    )
  }
  const disabledRemove = () => !hasPermissionWrite()

  const disabledMove = () => !hasPermissionWrite()

  const disabledConcederAcesso = () =>
    (pastasSelecionadas.length > 0 && arquivosSelecionados.length > 0) ||
    isSelected.length > 1 ||
    !hasPermissionWrite() ||
    hasPastaPai.length > 0

  const disabledRenomear = () =>
    !(
      isSelected.length === 1 &&
      hasWritePermission(isSelected[0].data) &&
      isSelected[0].data.isPasta
    )

  return {
    arquivosSelecionados,
    pastasSelecionadas,
    isSelected,
    disabledRemove,
    disabledMove,
    disabledConcederAcesso,
    disabledRenomear,
    handleDownload
  }
}
